// Platform Admin + School Admin — AI Marketing Video Generator (Google Veo 3.1)
import React, { useEffect, useState, useRef } from 'react';
import SchoolShowcaseWizard from './SchoolShowcaseWizard';
import AutoPublishModal from './AutoPublishModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCENE_PRESETS = [
  {
    id: 1,
    title: 'Scene 1 — Hook: Stressed Principal',
    prompt: 'Cinematic close-up of an Indian school principal in his late 40s wearing formal shirt, sitting at old wooden desk piled with attendance registers and fee files, warm evening golden sunlight through window casting light on his tired face, soft orange-amber color grading, shallow depth of field, slow camera push-in, 4K cinematic, documentary style, warm melancholic mood, realistic Indian faces and attire',
  },
  {
    id: 2,
    title: 'Scene 2 — Problem: Chaos of Manual Work',
    prompt: 'Cinematic montage with rapid cuts showing chaos in Indian school office: female teacher in saree rushing with thick attendance register, worried Indian parent on smartphone frowning, male school clerk buried under fee receipts at counter, traditional Indian school hallway with tube lights, handheld camera, tense documentary mood, warm tungsten indoor lighting, authentic Indian school setting with Hindi signage',
  },
  {
    id: 3,
    title: 'Scene 3 — Discovery: The Platform',
    prompt: 'Cinematic shot of same Indian principal opening laptop in warm-lit office, soft blue glow from screen illuminating his face, expression transforming from tired to hopeful, camera slowly dollying closer, warm orange backlight softening into cool blue screen light, bokeh background with light orbs, shallow depth of field, slow-motion smile forming, hopeful uplifting mood, 4K cinematic, realistic Indian man',
  },
  {
    id: 4,
    title: 'Scene 4 — Transformation: In Action',
    prompt: 'Cinematic sequence, bright and uplifting, young Indian female teacher in modern saree using tablet to take attendance in vibrant Indian classroom full of smiling uniformed students, Indian mother at home in modest kitchen looking at smartphone receiving school notification and smiling warmly, natural daylight, warm golden-green color palette, joyful energetic mood, authentic Indian school uniforms, 4K cinematic',
  },
  {
    id: 5,
    title: 'Scene 5 — CTA: Hero Close',
    prompt: 'Cinematic hero shot, same Indian principal confidently standing in school courtyard at golden hour, looking at camera with warm smile, Indian school building with Hindi विद्यालय signage visible, Indian tricolor flag fluttering in distance, sunlight rim lighting on shoulders, shallow depth of field, slow push-in, aspirational inspirational mood, warm golden-orange color grade, 4K cinematic',
  },
];

export default function VeoVideoPanel({ token, user, language = 'hi' }) {
  const [mode, setMode] = useState('showcase');  // 'showcase' | 'custom'
  const [jobs, setJobs] = useState([]);
  const [publishingJob, setPublishingJob] = useState(null);  // job currently in publish modal
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    prompt: '',
    aspect_ratio: '16:9',
    resolution: '720p',
    title: '',
    scene_number: null,
  });
  const [msg, setMsg] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/api/veo/jobs?limit=30`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setJobs(d.items || []);
    } catch (e) { console.warn('load jobs:', e); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 15000);  // refresh every 15s
    return () => clearInterval(pollRef.current);
  }, []);

  const pickScene = (scene) => {
    setForm({
      ...form,
      prompt: scene.prompt,
      title: scene.title,
      scene_number: scene.id,
    });
  };

  const submit = async () => {
    if (!form.prompt || form.prompt.length < 10) {
      setMsg({ type: 'error', text: language === 'hi' ? 'Prompt कम से कम 10 characters का होना चाहिए' : 'Prompt must be at least 10 characters' });
      return;
    }
    setSubmitting(true); setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/veo/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setMsg({ type: 'success', text: `✅ Job submitted — ${d.job_id} (${d.key_source})` });
        setForm({ ...form, prompt: '', title: '', scene_number: null });
        await load();
      } else {
        setMsg({ type: 'error', text: d.detail || 'Submit failed' });
      }
    } catch (e) { setMsg({ type: 'error', text: 'Error: ' + e.message }); }
    setSubmitting(false);
  };

  const badge = (s) => {
    const map = {
      queued: 'bg-slate-100 text-slate-700',
      processing: 'bg-blue-100 text-blue-700 animate-pulse',
      scheduled: 'bg-amber-100 text-amber-700',
      cancelled: 'bg-gray-200 text-gray-600',
      completed: 'bg-green-100 text-green-700',
      partial_failure: 'bg-amber-100 text-amber-800',
      failed: 'bg-red-100 text-red-700',
      timeout: 'bg-amber-100 text-amber-700',
    };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${map[s] || 'bg-gray-100'}`}>{s?.toUpperCase()}</span>;
  };

  return (
    <div className="space-y-4" data-testid="veo-video-panel">
      {/* Header */}
      <div>
        <h3 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Outfit' }}>
          🎬 {language === 'hi' ? 'AI Marketing Videos — Google Veo 3.1' : 'AI Marketing Videos — Google Veo 3.1'}
        </h3>
        <p className="text-xs text-gray-500">
          {user?.role === 'platform_admin'
            ? (language === 'hi' ? 'Platform Admin key से videos generate करें (free tier: 3 videos/day, billing active होने पर unlimited)' : 'Uses platform env key (free tier: 3/day; unlimited after billing active)')
            : (language === 'hi' ? 'School की Gemini key से videos generate होंगे। Settings → Integrations में key save करें।' : 'Uses school\'s own Gemini key. Save key in Settings → Integrations.')}
        </p>
      </div>

      {/* Mode Tabs */}
      <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1" data-testid="veo-mode-tabs">
        <button
          onClick={() => setMode('showcase')}
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${mode === 'showcase' ? 'bg-white shadow text-purple-700' : 'text-slate-600 hover:text-slate-900'}`}
          data-testid="veo-mode-showcase"
        >
          ⚡ {language === 'hi' ? '1-Click School Showcase' : '1-Click School Showcase'}
        </button>
        <button
          onClick={() => setMode('custom')}
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${mode === 'custom' ? 'bg-white shadow text-orange-700' : 'text-slate-600 hover:text-slate-900'}`}
          data-testid="veo-mode-custom"
        >
          ✍️ {language === 'hi' ? 'Custom Prompt' : 'Custom Prompt'}
        </button>
      </div>

      {mode === 'showcase' && <SchoolShowcaseWizard token={token} user={user} language={language} />}

      {mode === 'custom' && (
      <>
      {/* Scene Presets */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
        <p className="text-xs font-semibold text-orange-800 mb-2 uppercase tracking-wider">
          {language === 'hi' ? 'Marketing Video Scene Presets (60-sec storyline)' : 'Marketing Video Scene Presets (60-sec storyline)'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {SCENE_PRESETS.map((s) => (
            <button
              key={s.id}
              onClick={() => pickScene(s)}
              className={`text-left text-xs px-3 py-2 rounded-lg border transition ${form.scene_number === s.id ? 'bg-orange-500 text-white border-orange-600' : 'bg-white hover:bg-orange-100 border-orange-200'}`}
              data-testid={`scene-preset-${s.id}`}
            >
              <div className="font-bold">Scene {s.id}</div>
              <div className="text-[10px] opacity-80 line-clamp-2">{s.title.split('—')[1]?.trim()}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl p-5 border space-y-3">
        <input
          type="text"
          placeholder={language === 'hi' ? 'Title (optional)' : 'Title (optional)'}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg text-sm"
          data-testid="veo-title"
        />
        <textarea
          value={form.prompt}
          onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          rows={5}
          placeholder={language === 'hi' ? 'Video prompt (English में detailed scene description — min 10 chars, max 2000)' : 'Video prompt (English, detailed scene — min 10 chars, max 2000)'}
          className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
          data-testid="veo-prompt"
        />
        <div className="flex flex-wrap gap-3">
          <label className="text-xs">
            <span className="text-gray-500 block mb-1">Aspect Ratio</span>
            <select value={form.aspect_ratio} onChange={(e) => setForm({ ...form, aspect_ratio: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="veo-aspect">
              <option value="16:9">16:9 (YouTube)</option>
              <option value="9:16">9:16 (Shorts/Reels)</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="text-gray-500 block mb-1">Resolution</span>
            <select value={form.resolution} onChange={(e) => setForm({ ...form, resolution: e.target.value })} className="px-2 py-1.5 border rounded text-sm" data-testid="veo-resolution">
              <option value="720p">720p (Fast, cheaper)</option>
              <option value="1080p">1080p (Higher cost)</option>
            </select>
          </label>
          <div className="ml-auto flex items-end">
            <button
              onClick={submit}
              disabled={submitting || !form.prompt || form.prompt.length < 10}
              className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg text-sm disabled:opacity-50"
              data-testid="veo-submit-btn"
            >
              {submitting ? 'Submitting…' : (language === 'hi' ? '🎬 Video Generate करें' : '🎬 Generate Video')}
            </button>
          </div>
        </div>
        {msg && (
          <div className={`text-xs px-3 py-2 rounded ${msg.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="veo-msg">
            {msg.text}
          </div>
        )}
      </div>
      </>
      )}

      {/* Jobs list */}
      <div className="bg-white rounded-xl p-5 border">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-gray-800">
            {language === 'hi' ? 'आपके Jobs' : 'Your Jobs'} ({jobs.length})
          </h4>
          <button onClick={load} className="text-xs px-2 py-1 bg-slate-100 rounded hover:bg-slate-200" data-testid="veo-refresh">↻ Refresh</button>
        </div>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="text-sm text-gray-500">{language === 'hi' ? 'अभी कोई job नहीं है।' : 'No jobs yet.'}</p>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {jobs.map((j) => (
              <div key={j.id} className="border rounded-lg p-3 space-y-1" data-testid={`veo-job-${j.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {badge(j.status)}
                      <span className="text-sm font-semibold truncate">{j.title || `Job ${j.id.slice(-6)}`}</span>
                      <span className="text-[10px] text-gray-400">{j.aspect_ratio} · {j.resolution}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 line-clamp-2 mt-1">{j.prompt}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {new Date(j.created_at).toLocaleString()} · {j.created_by_name} · {j.key_source}
                      {j.file_size_bytes ? ` · ${(j.file_size_bytes / 1024 / 1024).toFixed(2)} MB` : ''}
                    </p>
                    {j.error && <p className="text-[11px] text-red-600 mt-1">❌ {j.error}</p>}
                  </div>
                  {j.status === 'completed' && (
                    <div className="flex gap-2">
                      <a
                        href={`${API_URL}/api/veo/videos/${j.id}?token=${token}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 font-semibold"
                        data-testid={`veo-view-${j.id}`}
                      >▶ View</a>
                      <button
                        onClick={() => setPublishingJob(j)}
                        className="text-xs px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded hover:opacity-90 font-semibold"
                        data-testid={`veo-publish-${j.id}`}
                      >📢 Publish</button>
                    </div>
                  )}
                </div>
                {j.status === 'completed' && (
                  <video
                    src={`${API_URL}/api/veo/videos/${j.id}?token=${token}`}
                    controls
                    className="w-full max-w-lg rounded mt-2"
                    preload="metadata"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Auto-Publish Modal */}
      {publishingJob && (
        <AutoPublishModal
          token={token}
          job={publishingJob}
          language={language}
          onClose={() => setPublishingJob(null)}
        />
      )}
    </div>
  );
}
